import { useEffect, useRef, useState, useCallback } from 'react';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';

export default function WhatWeBuild() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoFadingRef = useRef(false);
  const [videoFading, setVideoFading] = useState(false);

  // Smooth crossfade loop
  const handleVideoEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play().then(() => {
      videoFadingRef.current = false;
      setVideoFading(false);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const fadeThreshold = 2;
      if (video.duration - video.currentTime < fadeThreshold && !videoFadingRef.current) {
        videoFadingRef.current = true;
        setVideoFading(true);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleVideoEnded);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleVideoEnded);
    };
  }, [handleVideoEnded]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, 1 - (rect.top / window.innerHeight)));
      section.style.setProperty('--scroll-progress', String(progress));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5;
    }
  }, []);

  useRevealOnScroll(sectionRef);

  const services = [
    {
      id: 1,
      icon: 'ri-terminal-box-line',
      title: 'Custom Software Development',
      description:
        'Bespoke applications across web, mobile and desktop — internal tools, client-facing products and full business systems, designed around your workflow and built to run in production.',
      tags: ['Web', 'Mobile', 'Desktop'],
    },
    {
      id: 2,
      icon: 'ri-stock-line',
      title: 'Trading Platform Development',
      description:
        'Engineering of trading platforms, algorithmic execution tools and the software around them — built for speed and reliability. We build the technology; the strategy and trading stay yours.',
      tags: ['High Frequency', 'Low Latency'],
    },
    {
      id: 3,
      icon: 'ri-brain-line',
      title: 'AI Development & Integration',
      description:
        'Generative and applied AI — assistants, automation, predictive models and data analysis — delivered on its own or wired directly into your platform and customer success tools.',
      tags: ['LLM', 'Predictive', 'Automation'],
    },
    {
      id: 4,
      icon: 'ri-user-heart-line',
      title: 'Customer Success Tools',
      description:
        'Software that supports the client lifecycle — onboarding, support and retention — built to fit your process and connect to the rest of your stack.',
      tags: ['Onboarding', 'Support', 'Retention'],
    },
  ];

  return (
    <section
      id="build"
      ref={sectionRef}
      className="relative w-full min-h-[80vh] md:min-h-screen flex items-center overflow-hidden bg-background-900"
    >
      {/* Section transition marker */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3 z-20">
        <div className="w-10 h-[1px] bg-gradient-to-r from-transparent to-accent-500/20"></div>
        <div className="w-1 h-1 rounded-full bg-accent-500/25"></div>
        <div className="w-10 h-[1px] bg-gradient-to-l from-transparent to-accent-500/20"></div>
      </div>

      {/* Background video */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          src="https://public.readdy.ai/ai/video_res/019f5a50-e672-7de2-919b-98f9d2e7a1fc.mp4"
          autoPlay
          muted
          playsInline
          className={`w-full h-full object-cover object-top transition-opacity duration-[3000ms] ease-in-out ${videoFading ? 'opacity-0' : 'opacity-70'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-900/65 via-background-900/20 to-background-900/65"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-900/50 via-transparent to-background-900/50"></div>
      </div>

      {/* Floating amber particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[12%] left-[8%] w-1 h-1 rounded-full bg-accent-500/35 animate-float" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-[28%] right-[14%] w-1.5 h-1.5 rounded-full bg-accent-400/25 animate-float" style={{ animationDelay: '2.5s', animationDuration: '10s' }}></div>
        <div className="absolute bottom-[18%] left-[18%] w-1 h-1 rounded-full bg-accent-500/22 animate-float-slow" style={{ animationDelay: '3.5s' }}></div>
        <div className="absolute bottom-[35%] right-[8%] w-1.5 h-1.5 rounded-full bg-accent-400/30 animate-float" style={{ animationDelay: '1.5s', animationDuration: '9s' }}></div>
        <div className="absolute top-[55%] left-[4%] w-1 h-1 rounded-full bg-accent-500/20 animate-float" style={{ animationDelay: '5s' }}></div>
        <div className="absolute top-[42%] right-[22%] w-1 h-1 rounded-full bg-accent-400/22 animate-float-slow" style={{ animationDelay: '6s' }}></div>
      </div>

      {/* Ambient glow orbs — scroll driven */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-accent-500/3 blur-[200px] animate-breathe"
          style={{ opacity: 'calc(0.25 + var(--scroll-progress, 0) * 0.4)', animationDuration: '12s' }}
        ></div>
        <div
          className="absolute bottom-1/4 right-1/4 w-[450px] h-[450px] rounded-full bg-accent-400/2 blur-[160px] animate-breathe"
          style={{ opacity: 'calc(0.2 + var(--scroll-progress, 0) * 0.25)', animationDuration: '8s', animationDelay: '3s' }}
        ></div>
      </div>

      {/* Floating decorative rings */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[18%] right-[12%] w-44 h-44 animate-breathe" style={{ animationDuration: '10s' }}>
          <div className="w-full h-full rounded-full border border-accent-500/5 animate-rotate-slow opacity-35"></div>
        </div>
        <div className="absolute bottom-[12%] left-[8%] w-36 h-36 animate-breathe" style={{ animationDuration: '8s', animationDelay: '3s' }}>
          <div className="w-full h-full rounded-full border border-accent-500/4 animate-rotate-slow opacity-25" style={{ animationDirection: 'reverse', animationDuration: '40s' }}></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-20">
            {/* Eyebrow */}
            <div data-reveal className="flex items-center justify-center gap-3 mb-8">
              <div className="w-8 h-[1px] bg-accent-500/40"></div>
              <span className="text-xs font-label tracking-[0.3em] uppercase text-accent-400">
                Our Services
              </span>
              <div className="w-8 h-[1px] bg-accent-500/40"></div>
            </div>

            <h2 data-reveal data-reveal-delay="0.1" className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-50 tracking-tight leading-[1.15]">
              Software
              {' '}
              <span className="text-shimmer">built around you</span>
            </h2>

          </div>

          {/* Services grid — 2x2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                data-reveal
                data-reveal-delay={`${0.3 + index * 0.1}`}
                className="group relative p-7 md:p-9 rounded-2xl border border-background-200/10 bg-background-950/25 backdrop-blur-md hover:border-accent-500/25 transition-all duration-500 overflow-hidden"
              >
                {/* Card hover glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute -inset-1 bg-gradient-to-br from-accent-500/4 via-transparent to-transparent rounded-2xl"></div>
                </div>

                {/* Service icon */}
                <div className="mb-6 w-12 h-12 rounded-xl bg-accent-500/8 border border-accent-500/15 flex items-center justify-center group-hover:bg-accent-500/12 group-hover:border-accent-500/25 transition-all duration-500">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${service.icon} text-accent-500 text-sm`}></i>
                  </div>
                </div>

                {/* Content */}
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-50 mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-foreground-300 leading-relaxed">
                  {service.description}
                </p>

                {/* Bottom accent line */}
                <div className="mt-7 w-full h-[1px] bg-gradient-to-r from-accent-500/20 via-accent-500/5 to-transparent group-hover:from-accent-500/40 group-hover:via-accent-500/15 transition-all duration-500"></div>
              </div>
            ))}
          </div>

          {/* Bottom separator */}
          <div className="mt-20 flex items-center justify-center gap-4">
            <div className="w-12 h-[1px] bg-accent-500/20"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-accent-500/40"></div>
            <div className="w-12 h-[1px] bg-accent-500/20"></div>
          </div>
        </div>
      </div>
    </section>
  );
}